import type { TranslatableFieldProps } from "@rothoblaas/design-system";
import { FieldPath, FieldPathValue, FieldValues, UseFormSetValue } from "react-hook-form";
import { Translation } from "../hooks/useTranslations";
import { getUITranslationLanguages, UI_TRANSLATION_LANGUAGE_CODES, UITranslationLanguageCode } from "../types";

type TranslationValue = string | null | undefined;

type BaseTranslationFieldName<TForm extends FieldValues, TLanguage extends UITranslationLanguageCode> = {
  [TKey in Extract<keyof TForm, string>]: TKey extends `${infer TBase}${Capitalize<TLanguage>}`
    ? TForm[TKey] extends TranslationValue
      ? TBase
      : never
    : never;
}[Extract<keyof TForm, string>];

type CommonTranslationFieldName<
  TForm extends FieldValues,
  TLanguages extends readonly UITranslationLanguageCode[] = typeof UI_TRANSLATION_LANGUAGE_CODES,
> = TLanguages extends readonly [
  infer TLanguage extends UITranslationLanguageCode,
  ...infer TRest extends readonly UITranslationLanguageCode[],
]
  ? Extract<BaseTranslationFieldName<TForm, TLanguage>, CommonTranslationFieldName<TForm, TRest>>
  : Extract<keyof TForm, string>;

/** Base names `x` for which the form has a string field `x<Lang>` for every translation language. */
export type TranslationFieldName<TForm extends FieldValues> = Extract<
  CommonTranslationFieldName<TForm>,
  Extract<keyof TForm, string>
>;

const getTranslationFieldKey = (field: string, language: UITranslationLanguageCode) =>
  `${field}${language.charAt(0).toUpperCase()}${language.slice(1)}`;

/**
 * Binds the design-system `TranslatableField` to a react-hook-form form whose translations
 * live in flat fields (`nameIt`, `nameDe`, ...). Spread the result onto `<TranslatableField>`.
 */
export const translatableFieldProps = <TForm extends FieldValues>(
  field: TranslationFieldName<TForm>,
  formValues: TForm,
  setFormValue: UseFormSetValue<TForm>,
  translation: Translation,
): Pick<TranslatableFieldProps, "languages" | "values" | "onChange" | "dialogTitle"> => ({
  languages: getUITranslationLanguages(translation).map((language) => ({
    code: language.value,
    label: language.display,
  })),
  values: Object.fromEntries(
    UI_TRANSLATION_LANGUAGE_CODES.map((language) => [
      language,
      (formValues[getTranslationFieldKey(field, language)] as TranslationValue) ?? "",
    ]),
  ),
  dialogTitle: translation.common.translations,
  onChange: (nextValues) => {
    UI_TRANSLATION_LANGUAGE_CODES.forEach((language) => {
      const key = getTranslationFieldKey(field, language) as FieldPath<TForm>;
      setFormValue(key, (nextValues[language] ?? "") as FieldPathValue<TForm, typeof key>, { shouldDirty: true });
    });
  },
});
